import { notification } from 'antd';
import { EditorState } from 'draft-js';
import { useMemo } from 'react';

import { editorStateToHtml } from '../../../../foundation/components/rich_text_editor/RichTextEditor';
import { useModalForm } from '../../../../foundation/hooks';
import { displayUnAuthorizedNotification } from '../../../../foundation/utils/notifications';
import { useGetPredictionToolsQuery } from '../../../api/globalDataApi';
import { useGetVariantPredictionToolsQuery } from '../../../api/variantApi';
import { useAddVariantPredictionToolMutation } from '../../../api/variantApi';
import { NOTIFICATION_MESSAGES, VARIANT_STAGE_TYPES } from '../../constants';
import {
  ALLOWED_PREDICTION_RESULT_SELECT_TOOLS,
  buildPredictionResultChoices,
  selectLabelFilterOption,
} from '../../utils';

interface UseSilicoToolModalLogicProps {
  variant_id: string;
  closeModal: () => void;
}

/**
 * Custom hook that encapsulates all the business logic for SilicoToolModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useSilicoToolModalLogic = ({
  variant_id,
  closeModal,
}: UseSilicoToolModalLogicProps) => {
  const {
    isLoading: isSubmitting,
    setIsLoading: setIsSubmitting,
    user,
    stageInfo,
    getIsSaveDisabled,
    createModalOpts,
  } = useModalForm({
    onClose: closeModal,
    successMessage: NOTIFICATION_MESSAGES.PREDICTION_TOOL_SAVED,
    errorMessage: NOTIFICATION_MESSAGES.PREDICTION_TOOL_SAVE_FAILED,
    stageType: VARIANT_STAGE_TYPES.MOLECULAR_IMPACT,
  });

  const { data: predictionTools = [] } = useGetPredictionToolsQuery();

  // RTK Query hook for variant prediction tools
  useGetVariantPredictionToolsQuery({ variant_id }, { skip: !variant_id });

  // Mutation hook returns a [trigger, result] tuple.
  // After a successful save, invalidatesTags for VariantPredictions fires and
  // RTK Query refetches the tools list automatically
  const [addVariantPredictionToolMutation] =
    useAddVariantPredictionToolMutation();

  // Map the global tools list to Ant Select options (label/value)
  const selectOptions = useMemo(
    () => predictionTools.map((t: any) => ({ label: t.name, value: t.id })),
    [predictionTools],
  );

  // Helper to derive selected tool definition from current form value
  const getSelectedToolDef = (toolId: number | undefined) =>
    predictionTools.find((t: any) => t.id === Number(toolId));

  // Ant Modal configuration for the add-tool dialog
  const modalOpts = {
    ...createModalOpts('Please select an insilico prediction tool from list'),
  };

  const initialValues = {
    predictiontool_id: undefined,
    score: '',
    prediction_result: '',
    comment: EditorState.createEmpty(),
  };

  /**
   * Get prediction result choices and determine if select should be used
   * based on the selected tool
   */
  const getPredictionResultConfig = (toolId: number | undefined) => {
    const selectedToolDef = getSelectedToolDef(toolId);
    const predictionResultChoices = buildPredictionResultChoices(
      (selectedToolDef as any)?.choices_json || [],
    );
    const shouldUsePredictionResultSelect =
      !!selectedToolDef &&
      ALLOWED_PREDICTION_RESULT_SELECT_TOOLS.has(
        (selectedToolDef as any).name,
      ) &&
      predictionResultChoices.length > 0;

    return {
      selectedToolDef,
      predictionResultChoices,
      shouldUsePredictionResultSelect,
    };
  };

  // Create the new entry on the server and refresh the variant's tools list afterwards
  const handleSubmit = async (values: any) => {
    if (!user) {
      displayUnAuthorizedNotification();
      return;
    }

    if (!variant_id) {
      notification.error({
        message: NOTIFICATION_MESSAGES.ERROR,
        description: NOTIFICATION_MESSAGES.VARIANT_ID_MISSING,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Convert rich-text EditorState to HTML before sending to API
      const commentHtml =
        values.comment && typeof values.comment !== 'string'
          ? editorStateToHtml(values.comment)
          : values.comment;

      await addVariantPredictionToolMutation({
        variant_id: Number(variant_id),
        requestData: {
          predictiontool_id: Number(values.predictiontool_id),
          score: values.score || null,
          prediction_result: values.prediction_result || null,
          comment: commentHtml || null,
        },
      }).unwrap();

      // invalidatesTags fires automatically — RTK Query refetches VariantPredictions

      notification.success({
        message: NOTIFICATION_MESSAGES.SUCCESS,
        description: NOTIFICATION_MESSAGES.PREDICTION_TOOL_SAVED,
      });

      closeModal();
    } catch (error) {
      // BIOFRO-80 ABS review: do NOT call notification.error() here.
      // The Axios response interceptor already shows a toast on every
      // failed request; a second one would duplicate the UI feedback.
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    user,
    stageInfo,
    selectOptions,
    modalOpts,
    initialValues,
    getIsSaveDisabled,
    getPredictionResultConfig,
    handleSubmit,
    selectLabelFilterOption,
  };
};

export default useSilicoToolModalLogic;
